import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} FeaturebaseState
 * @property {'authenticate' | 'changelog' | null} task
 * @property {{ unreadChanges: number }} changelog
 *
 * @typedef {import('../slice.types').BaseSlice & FeaturebaseState} FeaturebaseSlice
 *
 * @typedef {Object} IntegrationsSlice
 * @property {FeaturebaseSlice} featurebase - The Featurebase SDK state.
 */

/** @type {IntegrationsSlice} */
export const initialIntegrationsState = {
  featurebase: {
    status: 'init',
    errors: [],
    task: null,
    changelog: { unreadChanges: 0 }
  },
  integrations: [],
  integration_categories: [],
  integrationDetail: null,
  agencyIntegrationDetail: null,
  agencyIntegrations: [],
  agencyIntegrationsByIntegrationId: [],
  availablePortals: [],
  selectedPortals: [],
  assignedPortals: [],
  availableClients: [],
  assignedClients: [],
  portalIntegrations: {},
  portalIntegrationDetail: null
}

/** @enum {string} */
export const INTEGRATIONS_ACTIONS = {
  SET_FEATUREBASE_STATUS: 'SET_FEATUREBASE_STATUS',
  SET_FEATUREBASE_ERRORS: 'SET_FEATUREBASE_ERRORS',
  SET_FEATUREBASE_TASK: 'SET_FEATUREBASE_TASK',
  SET_FEATUREBASE_CHANGELOG: 'SET_FEATUREBASE_CHANGELOG',
  FETCH_INTEGRATIONS: 'FETCH_INTEGRATIONS',
  FETCH_INTEGRATION: 'FETCH_INTEGRATION',
  FETCH_AGENCY_INTEGRATION: 'FETCH_AGENCY_INTEGRATION',
  FETCH_AGENCY_INTEGRATIONS: 'FETCH_AGENCY_INTEGRATIONS',
  FETCH_AGENCY_INTEGRATIONS_BY_INTEGRATION_ID: 'FETCH_AGENCY_INTEGRATIONS_BY_INTEGRATION_ID',
  FETCH_PORTALS_FOR_INTEGRATION: 'FETCH_PORTALS_FOR_INTEGRATION',
  SET_PORTALS_FOR_INTEGRATION: 'SET_PORTALS_FOR_INTEGRATION',
  FETCH_ASSIGNED_PORTALS_FOR_INTEGRATION: 'FETCH_ASSIGNED_PORTALS_FOR_INTEGRATION',
  FETCH_CLIENTS_FOR_INTEGRATION: 'FETCH_CLIENTS_FOR_INTEGRATION',
  FETCH_ASSIGNED_CLIENTS_FOR_INTEGRATION: 'FETCH_ASSIGNED_CLIENTS_FOR_INTEGRATION',
  FETCH_PORTAL_INTEGRATIONS: 'FETCH_PORTAL_INTEGRATIONS',
  FETCH_PORTAL_INTEGRATION: 'FETCH_PORTAL_INTEGRATION',
  CREATE_PORTAL_INTEGRATION: 'CREATE_PORTAL_INTEGRATION',
  UPDATE_PORTAL_INTEGRATION: 'UPDATE_PORTAL_INTEGRATION',
  CLEAR_AGENCY_INTEGRATION_DETAIL: 'CLEAR_AGENCY_INTEGRATION_DETAIL'
}

export const featurebaseTaskPropTypes = PropTypes.oneOf(['authenticate', 'changelog', null])
export const featurebaseChangelogPropTypes = PropTypes.shape({ unreadChanges: PropTypes.number })
export const featurebaseSlicePropTypes = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  task: featurebaseTaskPropTypes,
  changelog: featurebaseChangelogPropTypes
})
export const integrationsSlicePropTypes = PropTypes.shape({
  featurebase: featurebaseSlicePropTypes
})

/**
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: FeaturebaseSlice['status'] }} FeaturebaseStatusAction
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: FeaturebaseSlice['errors'] }} FeaturebaseErrorsAction
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: FeaturebaseSlice['task'] }} FeaturebaseTaskAction
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: Partial<FeaturebaseSlice['changelog']> }} FeaturebaseChangelogAction
 *
 * @typedef {FeaturebaseStatusAction | FeaturebaseErrorsAction | FeaturebaseTaskAction | FeaturebaseChangelogAction } IntegrationsAction
 * */
